<template>
  <BlocksCard class="common-documents-viewer">
    <div ref="documentViewerRef">
      <!-- Gallery heading -->
      <h2 v-if="heading" class="__heading">
        {{ heading }}
      </h2>

      <ul>
        <li v-for="(document, key) in documents" :key="`document-${key}`">
          <a :href="document.url" :aria-label="document.caption" target="_blank" @click="triggerAnalyticsTracking('click', document.url)">
            <img v-lazy-load  :data-src="getDisplayIconUrl(document)" :alt="`${document.caption} document icon`">
            {{ document.caption }}
          </a>
        </li>
      </ul>
    </div>
  </BlocksCard>
</template>

<script lang="ts" setup>
import {PropType, Ref} from 'vue';
import {ImageItem} from '~/@types/common';
import {AnalyticsDataProps} from '~/@types/analytics';

// PROPS Definitions
//----------------------------------------------------------------------------------------------------------------------
const props = defineProps({
  heading: {
    type: String,
    default: '',
  },
  documents: {
    type: Array as PropType<ImageItem[]>,
    default: () => [],
  },
  analyticsEventData: {
    type: [Object, null] as PropType<AnalyticsDataProps>,
    default: () => null,
  },
});

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const {$analyticsTrack} = useNuxtApp();
const documentViewerRef = ref<HTMLElement | null>(null);
let observer: Ref<IntersectionObserver | null> = ref(null);
const isSectionVisible: Ref<boolean> = ref(false);


/**
 * @_MOUNTED Hook
 */
onMounted(() => {
  initViewTracker();
});

/**
 * @_WATCH images
 */
watch(() => props.documents, (newVal, oldVal) => {
  if (newVal && newVal !== oldVal) {
    initViewTracker(true);
  }
});

/**
 * Track when the section is visible in the viewport
 *
 * @param forceRefresh
 */
function initViewTracker (forceRefresh = false) {
  if (forceRefresh) {
    // Stop observing the element when the component is unmounted
    if (documentViewerRef.value && observer.value) {
      observer.value.unobserve(documentViewerRef.value);
    }
  }

  // Only proceed if documentViewerRef exists and Observer is supported
  if (documentViewerRef.value && 'IntersectionObserver' in window) {
    observer.value = new IntersectionObserver((entries) => {
      // entries[0] is this component
      if (entries[0].isIntersecting && !isSectionVisible.value) {
        triggerAnalyticsTracking();
        isSectionVisible.value = true;
      }
    }, {
      threshold: 0.8, // adjust this value to control when the observer callback should be fired
    });

    // Start observing the element
    observer.value.observe(documentViewerRef.value);
  }
}

/**
 * @_BEFORE_UNMOUNT Hook
 */
onBeforeUnmount(() => {
  // Stop observing the element when the component is unmounted
  if (documentViewerRef.value && observer.value) {
    observer.value.unobserve(documentViewerRef.value);
  }
});

/**
 * Trigger analytics tracking
 */
function triggerAnalyticsTracking (action: 'view' | 'click' = 'view', fileUrl?: string) {
  // Track analytics event
  $analyticsTrack({
    action: action,
    property: 'document',
    entity: props.analyticsEventData?.entity,
    entitySlug: props.analyticsEventData?.slug,
    displayedLocation: props.analyticsEventData?.displayedLocation,
    eventData: {
      userIntention: action === 'view' ? 'passive' : 'active',
      component: 'document-viewer',
      target: '_blank',
      fileUrl: fileUrl,
    },
  });
}

/**
 * Get display image url
 * @param displayFile
 */
function getDisplayIconUrl (displayFile: ImageItem) {
  const lastDotIndex = displayFile.url.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return ''; // No extension found
  }
  const extension = displayFile.url.slice(lastDotIndex + 1);

  switch (extension) {
    case 'pdf':
      return '/images/file-icons/pdf.png';
    case 'doc':
    case 'docx':
      return '/images/file-icons/doc.png';
    case 'xls':
    case 'xlsx':
    case 'csv':
      return '/images/file-icons/xls.png';
    case 'txt':
      return '/images/file-icons/txt.png';
    default:
        return '/images/file-icons/documents.png';
  }
}
</script>

<style lang="scss" scoped>
.common-documents-viewer {
  li a {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    color: #000;
    text-decoration: none;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
    transition: all 0.2s ease-in-out;

    &:hover {
      color: rgb(var(--primary));
      transform: scale(1.02);
      transition: all 0.2s ease-in-out;
    }

    img {
      margin-right: 12px;
      width: 38px;
      height: auto;
    }
  }
}
</style>
